import { useId, useState } from "react";
import { Link } from "react-router-dom";
import type { Crm } from "../../lib/crm";
import type { AnswerIntent, ContentItem, HumanTask, Json, JsonObject, ReplyIntent } from "../../lib/types";
import { useAsync } from "../../lib/useAsync";
import { fmtDate, fmtDateTime, humanise } from "../../lib/format";
import { plainProblem } from "../../lib/plainHealth";
import { ENGINE_TOWN } from "../../lib/engineTown.generated";
import { flatten, KEY_LABELS, pathLabel } from "../../lib/config";
import { Async, Empty, KeyValues, Notice, ReasonDialog, StatusChip } from "../../components/ui";
import { PlaybookDeck } from "../../components/PlaybookDeck";
import { Timeline } from "../../components/Timeline";
import { AiSearchChecklist, FactCheckSummary, isArticle, SourceList, VisualStrip, VoiceFlags } from "../../components/ContentParts";
import { ROUTE_BY_KEY, type RouteKey } from "../../lib/routes";
import { EntityPlaybook } from "../../components/FlavourChip";
import { newAddress } from "../../lib/nav";

interface CardProps {
  crm: Crm;
  task: HumanTask;
  onDone: () => void;
}

function JsonList({ value }: { value: Json | undefined }) {
  const rows = [...flatten(value ?? {}).entries()];
  if (!rows.length) return <p className="muted">No details.</p>;
  return <KeyValues rows={rows.map(([k, v]) => [pathLabel(k), v])} />;
}

/** Approve and reject buttons with an optional note on rejection. */
export function Decide({ approveLabel, onApprove, onReject, busy, approveDisabled }: { approveLabel: string; onApprove: () => Promise<void>; onReject: (note: string) => Promise<void>; busy?: boolean; approveDisabled?: boolean }) {
  const [rejecting, setRejecting] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <>
      <Notice text={error} tone="bad" />
      <div className="action-bar">
        <button type="button" className="btn" disabled={working || busy} onClick={() => setRejecting(true)}>
          Reject
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={working || busy || approveDisabled}
          onClick={async () => {
            setWorking(true);
            setError(null);
            try {
              await onApprove();
            } catch (e) {
              setError((e as Error).message);
              setWorking(false);
            }
          }}
        >
          {working ? "Saving…" : approveLabel}
        </button>
      </div>
      {rejecting && <ReasonDialog title="Reject and explain" label="Why? The agent uses this next time." confirm="Reject" onClose={() => setRejecting(false)} onConfirm={onReject} />}
    </>
  );
}

// 0.5 ------------------------------------------------------------------------------------------
export function ConfigTask({ crm, task, onDone }: CardProps) {
  const version = Number(task.payload.version);
  const data = useAsync(async () => {
    const [versions, tenant] = await Promise.all([crm.configVersions(), crm.tenant()]);
    return { draft: versions.find((v) => v.version === version && v.status === "draft") ?? null, active: (tenant?.config ?? null) as JsonObject | null };
  }, [crm, version]);
  return (
    <Async state={data}>
      {({ draft, active }) =>
        draft ? (
          <PlaybookDeck crm={crm} draft={draft} active={active} onDecided={onDone} />
        ) : (
          <Empty title={`Version ${version} is no longer a draft`}>
            <p>It was decided or withdrawn elsewhere.</p>
          </Empty>
        )
      }
    </Async>
  );
}

// 1.19 -----------------------------------------------------------------------------------------
/** Who a piece is for, chosen on its approval card: one of the business's audiences, or everyone ("general"). An
 * audience that does not use the piece's route can't be picked here: add the route to it in the playbook first. */
export function AudiencePicker({ crm, item, onChanged }: { crm: Crm; item: ContentItem; onChanged?: (audience: string) => void }) {
  const id = useId();
  const audiences = useAsync(() => crm.audiences(), [crm]);
  const [value, setValue] = useState<string>(item.audience ?? "");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ text: string; tone: "ok" | "bad" } | null>(null);
  const route = (item.route ?? null) as RouteKey | null;
  const list = audiences.data?.audiences ?? [];
  const labelOf = (key: string) => (key === "general" ? "Everyone (general)" : (list.find((a) => a.key === key)?.label ?? humanise(key)));
  const change = async (next: string) => {
    if (!next || next === value) return;
    const before = value;
    setValue(next);
    setBusy(true);
    setMessage(null);
    try {
      await crm.setContentAudience(item.id, next);
      setMessage({ text: `Saved: written for ${labelOf(next)}.`, tone: "ok" });
      onChanged?.(next);
    } catch (e) {
      setValue(before);
      setMessage({ text: (e as Error).message, tone: "bad" });
    } finally {
      setBusy(false);
    }
  };
  if (audiences.error) return null;
  return (
    <div className="audience-picker">
      <label className="field field-inline" htmlFor={id}>
        <span className="field-label">Written for</span>
        <select id={id} value={value} disabled={busy || audiences.loading} onChange={(e) => void change(e.target.value)}>
          {!value && <option value="">Choose an audience…</option>}
          {list.map((a) => {
            const off = Boolean(route && !a.routes.includes(route));
            return (
              <option key={a.key} value={a.key} disabled={off}>
                {off ? `${a.label} (not on ${ROUTE_BY_KEY[route!]?.label ?? route})` : a.label}
              </option>
            );
          })}
          <option value="general">Everyone (general)</option>
        </select>
      </label>
      {route && <p className="muted small">Goes out on {ROUTE_BY_KEY[route]?.label ?? humanise(route)}.</p>}
      <Notice text={message?.text ?? null} tone={message?.tone} />
    </div>
  );
}

// 1.21 -----------------------------------------------------------------------------------------
export function ContentTask({ crm, task, onDone }: CardProps) {
  const itemId = String(task.payload.content_item_id ?? task.entity_id ?? "");
  const item = useAsync(() => crm.contentItem(itemId), [crm, itemId]);
  const [body, setBody] = useState<string | null>(null);
  const id = useId();
  return (
    <Async state={item} empty={(c) => (!c ? <Empty title="This post no longer exists" /> : null)}>
      {(c) => {
        const text = body ?? c!.body ?? "";
        if (isArticle(c!)) return <ArticleApproval crm={crm} c={c!} onDone={onDone} />;
        return (
          <div>
            <p className="card-lede">
              {c!.network ? humanise(c!.network) : "Social"} post{c!.account ? ` as ${c!.account}` : ""}
              {c!.scheduled_at ? `, goes out ${fmtDateTime(c!.scheduled_at)}` : ", not scheduled yet"}. <StatusChip status={c!.status} />
            </p>
            {c!.title && <h4>{c!.title}</h4>}
            <AudiencePicker crm={crm} item={c!} />
            <VisualStrip crm={crm} assets={c!.assets} />
            <VoiceFlags meta={c!.meta} />
            <label className="field" htmlFor={id}>
              <span className="field-label">Post (you can edit before approving)</span>
              <textarea id={id} rows={Math.min(16, Math.max(6, text.split("\n").length + 1))} value={text} onChange={(e) => setBody(e.target.value)} />
            </label>
            <Decide
              approveLabel={body !== null && body !== c!.body ? "Approve with edits" : "Approve post"}
              onApprove={async () => {
                await crm.decideContent(c!.id, true, undefined, body !== null && body !== c!.body ? body : undefined);
                onDone();
              }}
              onReject={async (note) => {
                await crm.decideContent(c!.id, false, note);
                onDone();
              }}
            />
          </div>
        );
      }}
    </Async>
  );
}

/** 1.21 for an article: read-only, because edited text would skip the fact-check (1.3). Reject with a note instead. */
function ArticleApproval({ crm, c, onDone }: { crm: Crm; c: ContentItem; onDone: () => void }) {
  const passed = c.fact_check?.status === "passed";
  return (
    <div>
      <p className="card-lede">
        {humanise(c.kind)} article for the blog. Approving publishes it at the next daily run. <StatusChip status={c.status} />
      </p>
      <h4>{c.title ?? "Untitled article"}</h4>
      <AudiencePicker crm={crm} item={c} />
      {c.meta?.summary && <p>{c.meta.summary}</p>}
      <FactCheckSummary fc={c.fact_check} sources={c.sources} />
      <VoiceFlags meta={c.meta} />
      <VisualStrip crm={crm} assets={c.assets} />
      {/* card-check: not a card (a show-more of the article's text inside its task) */}
      <details className="disclosure" open>
        <summary>The article</summary>
        <div className="post-body article-body">{c.body}</div>
      </details>
      {/* card-check: not a card (a show-more of the sources inside the task) */}
      <details className="disclosure">
        <summary>Sources ({c.sources?.length ?? 0}) and structure</summary>
        <SourceList sources={c.sources} />
        <AiSearchChecklist meta={c.meta} />
      </details>
      <p className="muted small">To change the wording, reject it with a note: the content agent revises it and checks every claim again.</p>
      {!passed && <Notice tone="bad" text="This article has not passed its fact-check, so it can't be approved. Reject it with a note to send it back." />}
      <Decide
        approveLabel="Approve article"
        approveDisabled={!passed}
        onApprove={async () => {
          await crm.decideContent(c.id, true);
          onDone();
        }}
        onReject={async (note) => {
          await crm.decideContent(c.id, false, note);
          onDone();
        }}
      />
    </div>
  );
}

// 6.11 -----------------------------------------------------------------------------------------
export const INTENTS: { value: ReplyIntent; label: string; hint: string }[] = [
  { value: "interested", label: "Interested", hint: "Marks them as interested; the inbox agent drafts a reply for you to see" },
  { value: "question", label: "A question", hint: "The inbox agent drafts an answer" },
  { value: "later", label: "Not now, maybe later", hint: "Stops the emails; we get back in touch on a date you choose" },
  { value: "no", label: "Not interested", hint: "Stops the emails and closes the firm" },
  { value: "wrong_person", label: "Wrong person", hint: "Asks politely who the right contact is" },
  { value: "unsubscribe", label: "Unsubscribe", hint: "Never contact them again, on any channel" },
  { value: "out_of_office", label: "Out of office", hint: "Carries on once they're back" },
  { value: "other", label: "Something else", hint: "No automatic action; you deal with it" },
];

/** The intents a reply a person writes can carry (crm_answer_message); the rest are decided without writing. */
export const ANSWER_INTENTS: AnswerIntent[] = ["interested", "question", "wrong_person", "other"];

/** 6.10, 6.11 Write the reply yourself. Sending it is approving it (gate 4.10); the agent's own draft is replaced. */
export function WriteReply({ crm, messageId, initialBody = "", intent, onDone, sendLabel = "Send this reply" }: { crm: Crm; messageId: string; initialBody?: string; intent?: AnswerIntent; onDone: () => void; sendLabel?: string }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState(initialBody);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const id = useId();
  return (
    <div className="write-reply">
      <label className="field" htmlFor={`${id}-subject`}>
        <span className="field-label">Subject (optional; leave blank to reply under theirs)</span>
        <input id={`${id}-subject`} value={subject} onChange={(e) => setSubject(e.target.value)} />
      </label>
      <label className="field" htmlFor={`${id}-body`}>
        <span className="field-label">Your reply</span>
        <textarea id={`${id}-body`} rows={Math.max(6, body.split("\n").length + 2)} value={body} onChange={(e) => setBody(e.target.value)} />
      </label>
      <p className="muted small">It goes out from the mailbox their message arrived in, in the same email thread, at the next send. Sending it here counts as your approval.</p>
      <Notice text={error} tone="bad" />
      <div className="action-bar">
        <button
          type="button"
          className="btn btn-primary"
          disabled={busy || !body.trim()}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              await crm.answerMessage(messageId, body, { subject, intent });
              onDone();
            } catch (e) {
              setError((e as Error).message);
              setBusy(false);
            }
          }}
        >
          {busy ? "Saving…" : sendLabel}
        </button>
      </div>
    </div>
  );
}

export function ReplyTask({ crm, task, onDone }: CardProps) {
  const messageId = String(task.payload.message_id ?? task.entity_id ?? "");
  const msg = useAsync(() => crm.message(messageId), [crm, messageId]);
  const suggested = typeof task.payload.suggested_intent === "string" ? task.payload.suggested_intent : null;
  const [intent, setIntent] = useState<ReplyIntent | "">(INTENTS.some((i) => i.value === suggested) ? (suggested as ReplyIntent) : "");
  const [followUp, setFollowUp] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [writing, setWriting] = useState(false);
  const name = useId();
  const writable = !intent || (ANSWER_INTENTS as string[]).includes(intent);
  return (
    <Async state={msg} empty={(m) => (!m ? <Empty title="This message no longer exists" /> : null)}>
      {(m) => (
        <div>
          <p className="card-lede">
            <strong>{m!.person?.full_name ?? m!.from_value ?? "Unknown sender"}</strong>
            {m!.organisation?.name ? ` at ${m!.organisation.name}` : ""} replied by {m!.channel} on {fmtDateTime(m!.received_at)}.
          </p>
          <EntityPlaybook crm={crm} entityType="message" entityId={m!.id} due={typeof task.payload.reply_due_by === "string" ? task.payload.reply_due_by : null} />
          {suggested && (
            <p className="muted small">
              The inbox agent thinks this means “{(INTENTS.find((i) => i.value === suggested)?.label ?? humanise(suggested)).toLowerCase()}”
              {typeof task.payload.confidence === "number" ? `, but is only ${Math.round(Number(task.payload.confidence) * 100)}% sure` : ", but isn't sure"}. Read it and choose below.
            </p>
          )}
          <blockquote className="message-body">{m!.body ?? "(empty message)"}</blockquote>
          {m!.organisation_id && (
            // card-check: not a card (a show-more of the earlier conversation inside the task)
            <details className="disclosure">
              <summary>Earlier conversation</summary>
              <Timeline crm={crm} organisation={m!.organisation_id} limit={20} />
              <p>
                <Link to={`/contacts/companies/${m!.organisation_id}`}>Open the firm</Link>
              </p>
            </details>
          )}
          <fieldset className="radios">
            <legend>What did they mean?</legend>
            {INTENTS.map((i) => (
              <label key={i.value} className="radio">
                <input type="radio" name={name} value={i.value} checked={intent === i.value} onChange={() => setIntent(i.value)} />
                <span className="radio-text">
                  <span>{i.label}</span>
                  <span className="muted small">{i.hint}</span>
                </span>
              </label>
            ))}
          </fieldset>
          {intent === "later" && (
            <label className="field field-narrow" htmlFor={`${name}-date`}>
              <span className="field-label">Get back in touch on (optional)</span>
              <input id={`${name}-date`} type="date" value={followUp} onChange={(e) => setFollowUp(e.target.value)} />
            </label>
          )}
          <label className="field" htmlFor={`${name}-note`}>
            <span className="field-label">Note for the record (optional)</span>
            <input id={`${name}-note`} value={note} onChange={(e) => setNote(e.target.value)} />
          </label>
          <Notice text={error} tone="bad" />
          <div className="action-bar">
            <button
              type="button"
              className="btn btn-primary"
              disabled={!intent || busy}
              onClick={async () => {
                if (!intent) return;
                setBusy(true);
                setError(null);
                try {
                  await crm.decideReply(m!.id, intent, followUp ? new Date(`${followUp}T09:00:00`).toISOString() : undefined, note || undefined);
                  onDone();
                } catch (e) {
                  setError((e as Error).message);
                  setBusy(false);
                }
              }}
            >
              {busy ? "Saving…" : intent ? `Save: ${INTENTS.find((i) => i.value === intent)!.label.toLowerCase()}` : "Choose what they meant"}
            </button>
            <button type="button" className="btn" aria-expanded={writing} onClick={() => setWriting(!writing)}>
              {writing ? "Don't write a reply" : "Write the reply myself"}
            </button>
          </div>
          {writing &&
            (writable ? (
              <WriteReply crm={crm} messageId={m!.id} intent={(intent || undefined) as AnswerIntent | undefined} onDone={onDone} />
            ) : (
              <p className="muted">
                For “{INTENTS.find((i) => i.value === intent)!.label.toLowerCase()}”, save your choice instead: the agents stop the emails and send the right follow-up.
              </p>
            ))}
        </div>
      )}
    </Async>
  );
}

// 6.10 -----------------------------------------------------------------------------------------
const REVIEW_INTENT: Record<string, AnswerIntent> = { answer_question: "question", request_right_contact: "wrong_person", draft_reply: "interested" };

/** A reply the inbox agent must not answer itself: the objection library says so, or it isn't covered. */
export function ReviewReplyTask({ crm, task, onDone }: CardProps) {
  const p = task.payload;
  const messageId = String(p.message_id ?? task.entity_id ?? "");
  const msg = useAsync(() => crm.message(messageId), [crm, messageId]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const id = useId();
  const suggested = typeof p.suggested_answer === "string" ? p.suggested_answer : null;
  const draft = typeof p.draft === "string" ? p.draft : null;
  return (
    <Async state={msg} empty={(m) => (!m ? <Empty title="This message no longer exists" /> : null)}>
      {(m) => (
        <div>
          <p className="card-lede">
            <strong>{m!.person?.full_name ?? m!.from_value ?? "Unknown sender"}</strong>
            {m!.organisation?.name ? ` at ${m!.organisation.name}` : ""} wrote on {fmtDateTime(m!.received_at)}.
          </p>
          <EntityPlaybook crm={crm} entityType="message" entityId={m!.id} due={typeof p.reply_due_by === "string" ? p.reply_due_by : null} />
          <p className="muted small">The inbox agent left this one for you: {String(p.reason ?? "it needs a person")}.</p>
          <blockquote className="message-body">{m!.body ?? "(empty message)"}</blockquote>
          {suggested && (
            <p className="small">
              <strong>Objection library{p.objection ? ` (row ${String(p.objection)})` : ""}:</strong> {suggested}
            </p>
          )}
          <WriteReply crm={crm} messageId={m!.id} initialBody={draft ?? ""} intent={REVIEW_INTENT[String(p.task_kind ?? "")]} onDone={onDone} />
          {/* card-check: not a card (a small form, opened on demand, inside the task) */}
          <details className="disclosure">
            <summary>I've dealt with it another way</summary>
            <label className="field" htmlFor={id}>
              <span className="field-label">What you did (optional)</span>
              <input id={id} value={note} onChange={(e) => setNote(e.target.value)} />
            </label>
            <Notice text={error} tone="bad" />
            <button
              type="button"
              className="btn"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                setError(null);
                try {
                  await crm.completeTask(task.id, "done", note.trim() ? { note: note.trim() } : { handled: "elsewhere" });
                  onDone();
                } catch (e) {
                  setError((e as Error).message);
                  setBusy(false);
                }
              }}
            >
              {busy ? "Saving…" : "Mark as dealt with"}
            </button>
          </details>
        </div>
      )}
    </Async>
  );
}

// 9.6 -----------------------------------------------------------------------------------------
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** Offer terms as a readable list: lists stay lists, dates read as dates. */
function Terms({ terms }: { terms: JsonObject }) {
  const entries = Object.entries(terms ?? {});
  if (!entries.length) return <p className="muted">No terms recorded.</p>;
  return (
    <dl className="kv terms">
      {entries.map(([k, v]) => (
        <div key={k} className="kv-row">
          <dt>{KEY_LABELS[k] ?? humanise(k)}</dt>
          <dd>
            {Array.isArray(v) ? (
              <ul className="plain-list tight">
                {v.map((x, i) => (
                  <li key={i}>{typeof x === "string" ? x : JSON.stringify(x)}</li>
                ))}
              </ul>
            ) : typeof v === "string" && DATE_ONLY.test(v) ? (
              fmtDate(v)
            ) : v && typeof v === "object" ? (
              <JsonList value={v} />
            ) : (
              String(v ?? "—")
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export function DocumentTask({ crm, task, onDone }: CardProps) {
  const docId = String(task.payload.document_id ?? "");
  const doc = useAsync(() => crm.dealDocument(docId), [crm, docId]);
  return (
    <Async state={doc} empty={(d) => (!d ? <Empty title="This document no longer exists" /> : null)}>
      {(d) => (
        <div>
          <p className="card-lede">
            <strong>{d!.title}</strong> <StatusChip status={d!.status} />
          </p>
          <EntityPlaybook crm={crm} entityType="deal" entityId={d!.deal_id} />
          <p className="muted small">Read the terms. When you approve, the deal agent sends the {humanise(d!.kind).toLowerCase()} for signature. Nothing goes out before that.</p>
          <h4>Terms</h4>
          <Terms terms={d!.terms} />
          <Decide
            approveLabel={`Approve ${humanise(d!.kind).toLowerCase()}`}
            onApprove={async () => {
              await crm.decideDocument(d!.id, true);
              onDone();
            }}
            onReject={async (note) => {
              await crm.decideDocument(d!.id, false, note);
              onDone();
            }}
          />
        </div>
      )}
    </Async>
  );
}

// E7 -------------------------------------------------------------------------------------------
/**
 * E7 / E3 / E8 / E14 Something stopped working, in plain words (lib/plainHealth): which step, what's wrong and the one
 * thing to do, with a button for it. No job names, agent names or error text (E15: the owner isn't technical; the
 * details stay in Settings › Your engine).
 */
export function HealthTask({ crm, task, onDone }: CardProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const item = plainProblem(task, ENGINE_TOWN);
  const elsewhere = !item.action.to.startsWith("/ask");
  return (
    <div>
      <p className="card-lede">
        <strong>{item.title}.</strong>
      </p>
      <p className="muted small">{item.where}</p>
      {item.wrong && (
        <p>
          <strong>What's wrong:</strong> {item.wrong}
        </p>
      )}
      <p>
        <strong>What to do:</strong> {item.todo}
      </p>
      <Notice text={error} tone="bad" />
      <div className="action-bar">
        {elsewhere && (
          <Link className="btn btn-primary" to={item.action.to}>
            {item.action.label}
          </Link>
        )}
        <button
          type="button"
          className={elsewhere ? "btn" : "btn btn-primary"}
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              await crm.completeTask(task.id, "done", { seen: true });
              onDone();
            } catch (e) {
              setError((e as Error).message);
              setBusy(false);
            }
          }}
        >
          {busy ? "Saving…" : "Mark as sorted"}
        </button>
      </div>
    </div>
  );
}

// 2.32 Same firm? ------------------------------------------------------------------------------
const SAME_FIRM_WHY: Record<string, string> = {
  domain_match_name_differs: "They share a website, but the names are different.",
  name_match_domain_differs: "The names match, but the websites are different.",
};

function FirmFacts({ title, f }: { title: string; f: JsonObject }) {
  const rows: [string, string][] = [
    ["Name", String(f.name ?? "—")],
    ["Website", String(f.domain ?? f.website ?? "—")],
    ["Company number", String(f.company_number ?? "—")],
    ["Town", String(f.city ?? f.postcode ?? "—")],
  ];
  if (f.stage) rows.push(["Stage", humanise(String(f.stage))]);
  return (
    <div>
      <h4>{title}</h4>
      <KeyValues rows={rows} />
    </div>
  );
}

/** A firm found in the prospect database looks like one already in the CRM. Same: link them. Different: copy it. */
export function SameFirmTask({ crm, task, onDone }: CardProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const p = task.payload;
  const found = (p.found ?? {}) as JsonObject;
  const existing = (p.existing ?? {}) as JsonObject;
  const answer = async (same: boolean) => {
    setBusy(true);
    setError(null);
    try {
      await crm.answerSameFirm(task.id, same);
      onDone();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };
  return (
    <div>
      <p className="card-lede">
        <strong>Is this the same firm?</strong> {SAME_FIRM_WHY[String(p.why)] ?? ""}
      </p>
      <p className="muted small">The engine didn't guess. If it's the same firm, nothing new is added and anything you know about it still applies.</p>
      <div className="compare-grid">
        <FirmFacts title="Found in the prospect database" f={found} />
        <FirmFacts title="Already in your CRM" f={existing} />
      </div>
      {task.entity_id && (
        <p>
          <Link to={`/contacts/companies/${task.entity_id}`}>Open the firm in your CRM</Link>
        </p>
      )}
      <Notice text={error} tone="bad" />
      <div className="action-bar">
        <button type="button" className="btn" disabled={busy} onClick={() => void answer(false)}>
          Different firms
        </button>
        <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void answer(true)}>
          {busy ? "Saving…" : "Same firm"}
        </button>
      </div>
    </div>
  );
}

/**
 * The page a task names in `payload.to`, as it opens now: a task made before the modules moved may carry an old
 * address (/attract/actions/listings), which opens its new page (lib/nav.ts newAddress). Only an address inside the app.
 */
export function taskPage(task: Pick<HumanTask, "kind" | "payload">): string | null {
  const to = task.payload.to;
  if (typeof to !== "string" || !/^\/[a-z]/.test(to)) return null;
  return newAddress(to, task.kind);
}

// Anything else assigned to a human ------------------------------------------------------------
/** Payload keys that are for engineers, not the owner (E15). */
const TECHNICAL_KEYS = new Set(["agent", "job", "error", "last_error", "trace", "stack", "exception", "code", "status_code", "http_status", "run_id", "attempt", "attempts_log", "raw", "response", "request_id"]);

export function OtherTask({ crm, task, onDone }: CardProps) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const id = useId();
  const page = taskPage(task);
  const { to: _to, ...rest } = task.payload;
  // E15 No job names, agent names or error text for the owner: those stay in Settings › Account › What's running.
  const details = Object.fromEntries(Object.entries(page ? rest : task.payload).filter(([k]) => !TECHNICAL_KEYS.has(k)));
  return (
    <div>
      <JsonList value={details} />
      {page && (
        <p>
          <Link to={page}>Open the page for this</Link>
        </p>
      )}
      {task.entity_type === "organisation" && task.entity_id && (
        <p>
          <Link to={`/contacts/companies/${task.entity_id}`}>Open the firm</Link>
        </p>
      )}
      {task.entity_type === "person" && task.entity_id && (
        <p>
          <Link to={`/contacts/people/${task.entity_id}`}>Open the person</Link>
        </p>
      )}
      <label className="field" htmlFor={id}>
        <span className="field-label">Your answer or note (optional)</span>
        <textarea id={id} rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
      </label>
      <Notice text={error} tone="bad" />
      <div className="action-bar">
        <button
          type="button"
          className="btn btn-primary"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              await crm.completeTask(task.id, "done", note.trim() ? { note: note.trim() } : {});
              onDone();
            } catch (e) {
              setError((e as Error).message);
              setBusy(false);
            }
          }}
        >
          {busy ? "Saving…" : "Mark as done"}
        </button>
      </div>
    </div>
  );
}

// 1.6 Did the post go out? (review fix: publishing to Meta is safe to repeat). Ogma asked Instagram or Facebook to publish
// a post and could not tell whether it did, or Meta refused it three times. The answer goes to crm_settle_meta_post:
// it went out (with the link, if they have it), or it didn't and Ogma posts it again at the next run.
export function MetaPostCheckTask({ crm, task, onDone }: CardProps) {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const id = useId();
  const item = String(task.payload.content_item_id ?? task.entity_id ?? "");
  const network = humanise(String(task.payload.network ?? "Meta"));
  const answer = async (went: boolean) => {
    setBusy(true);
    setError(null);
    try {
      await crm.settleMetaPost(item, went, went && url.trim() ? url.trim() : null);
      onDone();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };
  return (
    <div>
      {task.payload.title ? <p><strong>{String(task.payload.title)}</strong></p> : null}
      <p>{String(task.payload.note ?? `Check ${network}: did this post go out?`)}</p>
      {task.payload.reason ? <p className="muted small">What happened: {String(task.payload.reason)}</p> : null}
      <label className="field" htmlFor={id}>
        <span className="field-label">The post's link, if it went out (optional)</span>
        <input id={id} type="url" value={url} placeholder="https://" onChange={(e) => setUrl(e.target.value)} />
      </label>
      <Notice text={error} tone="bad" />
      <div className="action-bar">
        <button type="button" className="btn btn-primary" disabled={busy || !item} onClick={() => void answer(true)}>
          It went out
        </button>
        <button type="button" className="btn" disabled={busy || !item} onClick={() => void answer(false)}>
          It didn't go out: post it again
        </button>
      </div>
    </div>
  );
}
